import React, { useEffect, useState } from 'react';
import { GraduationCap, Sparkles, ArrowUpRight, ShieldCheck, X } from 'lucide-react';

/**
 * ITHR Academy launch flag — hangs from a small flagpole pinned to the top-right
 * of the viewport (fixed) and sways gently like cloth. Clickable → ithr.online.
 * Auto-hides when the visitor scrolls past the hero so it doesn't distract from
 * the rest of the site, and can be dismissed via the small × control.
 * The sway keyframes live in /app/frontend/src/index.css (@keyframes flagSway).
 */
const AcademyFlagBanner = () => {
  const [dismissed, setDismissed] = useState(false);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if (dismissed) return undefined;
    const onScroll = () => setVisible(window.scrollY < 720);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [dismissed]);

  if (dismissed || !visible) return null;

  return (
    <div
      className="hidden md:block fixed top-24 right-60 lg:right-64 z-40"
      data-testid="ithr-academy-flag-wrapper"
    >
      <a
        href="https://ithr.online"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Enroll — ITHR Academy, the agentic AI training portal"
        data-testid="ithr-academy-flag"
        className="block group"
      >
        {/* Flagpole */}
        <div className="relative flex flex-col items-start">
          <div className="h-2.5 w-2.5 rounded-full bg-[#c9a84c] shadow-[0_0_10px_rgba(201,168,76,0.7)] ml-[6px]" />
          <div className="w-[2px] h-[360px] bg-gradient-to-b from-[#c9a84c] via-[#c9a84c]/70 to-[#c9a84c]/20 ml-[6px]" />

          {/* Cloth — swaying from the top edge */}
          <div
            className="absolute left-3 top-2.5 origin-top-left"
            style={{
              animation: 'flagSway 5.5s ease-in-out infinite',
              transformOrigin: 'top left',
              filter: 'drop-shadow(0 12px 22px rgba(21,40,70,0.28))',
            }}
          >
            <div className="relative w-[210px]">
              {/* Main cloth — navy gradient */}
              <div
                className="relative bg-gradient-to-br from-[#152846] via-[#1a3355] to-[#243d63] pl-4 pr-3 pt-3.5 pb-4 overflow-hidden rounded-tr-lg rounded-br-lg group-hover:from-[#1a3355] group-hover:to-[#c9a84c]/45 transition-colors duration-500"
                style={{
                  clipPath:
                    'polygon(0 0, 100% 0, 100% 88%, 90% 94%, 100% 100%, 0 100%)',
                }}
              >
                {/* Hover shimmer */}
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
                  style={{
                    background:
                      'linear-gradient(115deg, transparent 30%, rgba(201,168,76,0.35) 50%, transparent 70%)',
                    animation: 'flagShimmer 2.4s linear infinite',
                  }}
                />

                {/* NEW pill */}
                <div className="inline-flex items-center gap-1 px-2 py-0.5 mb-2 bg-[#c9a84c] text-[#152846] text-[9.5px] font-bold uppercase tracking-[0.14em] rounded-full">
                  <Sparkles size={9} strokeWidth={3} />
                  Newly Launched
                </div>

                {/* Title lockup */}
                <div className="flex items-start gap-2">
                  <div className="mt-0.5 shrink-0 w-7 h-7 rounded-md bg-[#c9a84c]/20 border border-[#c9a84c]/40 flex items-center justify-center">
                    <GraduationCap size={14} strokeWidth={2.5} className="text-[#c9a84c]" />
                  </div>
                  <div>
                    <div className="text-[9.5px] uppercase tracking-[0.18em] text-[#c9a84c]/80 font-semibold leading-tight">
                      ITHR Technologies presents
                    </div>
                    <div
                      className="text-white font-bold leading-tight text-base"
                      style={{ fontFamily: "'Playfair Display', serif" }}
                    >
                      ITHR Academy
                    </div>
                  </div>
                </div>

                <p className="mt-2 text-[10.5px] leading-snug text-[#e6dfd0]/90">
                  State-of-the-art <span className="text-[#c9a84c] font-semibold">Agentic AI</span> training portal — command AI, earn a verifiable Skills Passport.
                </p>

                {/* Endorsements */}
                <div className="mt-2.5 pt-2 border-t border-white/10">
                  <div className="flex items-center gap-1 text-[8.5px] uppercase tracking-[0.16em] text-[#c9a84c]/90 font-semibold mb-1">
                    <ShieldCheck size={9} strokeWidth={2.75} />
                    Authenticated &amp; endorsed by
                  </div>
                  <ul className="space-y-0.5 text-[10px] text-white/90 leading-tight">
                    <li className="flex items-start gap-1.5">
                      <span className="text-[#c9a84c] mt-[2px]">◆</span>
                      Canada School of Leadership
                    </li>
                    <li className="flex items-start gap-1.5">
                      <span className="text-[#c9a84c] mt-[2px]">◆</span>
                      Computer Society of India
                    </li>
                  </ul>
                </div>

                {/* CTA */}
                <div className="mt-2.5 inline-flex items-center gap-1 px-2.5 py-1.5 bg-[#c9a84c] text-[#152846] text-[10.5px] font-bold rounded-full group-hover:gap-2 transition-all">
                  Enroll at ithr.online
                  <ArrowUpRight size={11} strokeWidth={3} />
                </div>
              </div>

              {/* Grommets on the pole edge */}
              <div className="absolute -left-1 top-2.5 w-1.5 h-1.5 rounded-full bg-[#c9a84c] shadow-inner" />
              <div className="absolute -left-1 bottom-8 w-1.5 h-1.5 rounded-full bg-[#c9a84c] shadow-inner" />
            </div>
          </div>
        </div>
      </a>

      {/* Dismiss */}
      <button
        onClick={(e) => { e.stopPropagation(); setDismissed(true); }}
        aria-label="Dismiss ITHR Academy banner"
        data-testid="ithr-academy-flag-dismiss"
        className="absolute top-0 -right-1 z-10 w-5 h-5 rounded-full bg-[#faf6ee] border border-[#e6dfd0] text-[#5c6878] hover:text-[#152846] hover:border-[#c9a84c]/40 shadow-sm flex items-center justify-center transition-colors"
      >
        <X size={11} strokeWidth={2.5} />
      </button>
    </div>
  );
};

export default AcademyFlagBanner;

